import React, { useState, useEffect } from 'react';
import {
  Shield, Search, RefreshCw, AlertTriangle, CheckCircle2,
  XCircle, Clock, User, Monitor, Key, FileText, ChevronDown,
  ChevronRight, ArrowUpRight, Calendar, Filter, Download
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

const ACTION_LABELS = {
  'admin_cancel_subscription': 'Cancelled Subscription',
  'admin_reactivate_subscription': 'Reactivated Subscription',
  'admin_plan_override': 'Changed Plan',
  'admin_delete_user': 'Deleted User',
  'admin_reset_password': 'Reset Password',
  'admin_verify_email': 'Verified Email',
  'admin_clear_sessions': 'Cleared Sessions',
  'admin_toggle_lock': 'Toggled Account Lock',
  'admin_reset_account': 'Reset Account',
  'add_credits': 'Added Credits',
  'credit_issued': 'Issued Credit/Debit',
  'trial_extension': 'Extended Trial',
  'reset_usage': 'Reset Usage',
  'self_delete_account': 'Self-Deleted Account',
  'self_cancel_subscription': 'Cancelled Own Subscription',
  'self_reactivate_subscription': 'Reactivated Own Subscription',
  'self_plan_upgrade': 'Upgraded Plan',
  'self_plan_downgrade': 'Downgraded Plan',
  'password_changed': 'Changed Password',
  '2fa_enabled': 'Enabled 2FA',
  '2fa_disabled': 'Disabled 2FA',
  'registration_approved': 'Approved Registration',
  'registration_rejected': 'Rejected Registration',
  'crypto_payment_manual_verify': 'Verified Crypto Payment',
  'crypto_payment_rejected': 'Rejected Crypto Payment',
};

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);
  const [filters, setFilters] = useState({ actor_type: '', action: '', status: '', hours: 24 });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchLogs = async (p = 1) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.actor_type) params.append('actor_type', filters.actor_type);
    if (filters.action) params.append('action', filters.action);
    if (filters.status) params.append('status', filters.status);
    params.append('hours', filters.hours);
    params.append('page', p);
    params.append('limit', 50);

    try {
      const res = await api.get(`/api/admin/admin/audit-logs?${params}`);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setPage(p);
    } catch { toast.error('Failed to load audit logs'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchLogs(); }, []);

  const getStatusBadge = (status) => {
    const config = {
      success: { bg: 'var(--success-bg)', color: 'var(--success)', icon: <CheckCircle2 size={12} /> },
      failure: { bg: 'var(--error-bg)', color: 'var(--error)', icon: <XCircle size={12} /> },
      blocked: { bg: 'var(--warning-bg)', color: 'var(--warning)', icon: <AlertTriangle size={12} /> },
      pending: { bg: 'var(--info-bg)', color: 'var(--info)', icon: <Clock size={12} /> },
    };
    const c = config[status] || config.pending;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: c.bg, color: c.color }}>
        {c.icon} {status}
      </span>
    );
  };

  const getActorIcon = (type) => {
    const icons = { user: <User size={14} />, admin: <Shield size={14} />, system: <Monitor size={14} />, worker: <RefreshCw size={14} /> };
    return icons[type] || <Key size={14} />;
  };

  const formatTime = (ts) => {
    if (!ts) return ', ';
    const d = new Date(ts);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const parseDetails = (log) => {
    // Build description from available fields
    const parts = [];
    
    // Use the human-readable description if available
    if (log.details) {
      try {
        const d = typeof log.details === 'string' ? JSON.parse(log.details) : log.details;
        if (d.description) parts.push(d.description);
      } catch {}
    }
    
    // Show admin who performed the action
    if (log.admin_email) {
      parts.push(`By: ${log.admin_email}`);
    }
    
    // Show target
    if (log.target_name) {
      parts.push(`Target: ${log.target_name}`);
    }
    
    // Show old → new values
    if (log.old_value || log.new_value) {
      try {
        const old = log.old_value ? JSON.parse(log.old_value) : null;
        const news = log.new_value ? JSON.parse(log.new_value) : null;
        if (old && news) {
          const changes = [];
          const allKeys = new Set([...Object.keys(old || {}), ...Object.keys(news || {})]);
          allKeys.forEach(k => {
            if (old[k] !== news[k] && k !== 'reason') {
              changes.push(`${k}: ${old[k] || 'N/A'} → ${news[k] || 'N/A'}`);
            }
          });
          if (changes.length) parts.push(changes.join(' | '));
          if (news?.reason) parts.push(`Reason: ${news.reason}`);
        }
      } catch {}
    }
    
    return parts.length > 0 ? parts : [log.action];
  };

  const actionLabel = (action) => ACTION_LABELS[action] || action?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div className="page-content" style={{ maxWidth: '100%' }}>
      
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Shield size={22} color="var(--accent-primary)" /> Audit Logs
          </h2>
          <p style={{ color: 'var(--text-tertiary)', fontSize: 13, margin: 0 }}>
            {total.toLocaleString()} total events · Complete record of all administrative actions and security events
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={async () => {
            try {
              const res = await api.get('/api/admin/admin/audit-integrity');
              if (res.data.integrity_ok) {
                toast.success(`${res.data.total_checked} audit entries checked,  integrity verified`);
              } else {
                toast.error(`${res.data.tampered} of ${res.data.total_checked} entries may have been tampered`);
              }
            } catch { toast.error('Integrity check failed'); }
          }} style={{
            padding: '8px 14px', borderRadius: 8, border: '1px solid var(--success)',
            background: 'transparent', color: 'var(--success)', cursor: 'pointer',
            fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
          }} title="Verify cryptographic integrity of audit logs"><Shield size={13} /> Verify</button>
          <button onClick={async () => {
            const params = new URLSearchParams();
            if (filters.actor_type) params.append('actor_type', filters.actor_type);
            if (filters.action) params.append('action', filters.action);
            if (filters.status) params.append('status', filters.status);
            params.append('hours', filters.hours);
            params.append('format', 'csv');
            try {
              const res = await api.get(`/api/admin/admin/audit-logs?${params}`, { responseType: 'blob' });
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const a = document.createElement('a');
              a.href = url;
              a.download = `ileadx_audit_logs_${new Date().toISOString().slice(0,10)}.csv`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
              toast.success('Audit logs exported as CSV');
            } catch { toast.error('Export failed'); }
          }} style={{
            padding: '8px 14px', borderRadius: 8, border: '1px solid var(--accent-primary)',
            background: 'transparent', color: 'var(--accent-primary)', cursor: 'pointer',
            fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
          }}><Download size={13} /> Export CSV</button>
          <button onClick={() => fetchLogs(page)} style={{
            padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-primary)',
            background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer',
            fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
          }}><RefreshCw size={13} /> Refresh</button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={filters.actor_type} onChange={e => setFilters({...filters, actor_type: e.target.value})} style={filterStyle}>
          <option value="">All Actors</option>
          <option value="admin">Admin</option>
          <option value="user">User</option>
          <option value="system">System</option>
          <option value="worker">Worker</option>
        </select>
        <select value={filters.status} onChange={e => setFilters({...filters, status: e.target.value})} style={filterStyle}>
          <option value="">All Status</option>
          <option value="success">Success</option>
          <option value="failure">Failure</option>
          <option value="blocked">Blocked</option>
        </select>
        <select value={filters.hours} onChange={e => setFilters({...filters, hours: parseInt(e.target.value)})} style={filterStyle}>
          <option value={1}>Last hour</option>
          <option value={6}>Last 6 hours</option>
          <option value={24}>Last 24 hours</option>
          <option value={72}>Last 3 days</option>
          <option value={168}>Last 7 days</option>
        </select>
        <button onClick={() => fetchLogs(1)} style={{
          padding: '8px 16px', borderRadius: 8, border: '1px solid var(--accent-primary)',
          background: 'var(--accent-glow)', color: 'var(--accent-primary)', cursor: 'pointer',
          fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
        }}><Search size={14} /> Apply</button>
        <span style={{ color: 'var(--text-tertiary)', fontSize: 12, marginLeft: 'auto' }}>
          Page {page} · {total.toLocaleString()} events
        </span>
      </div>

      {/* Log List */}
      <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 16, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', minWidth: '100%' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60 }}><div className="loader" /></div>
        ) : logs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-tertiary)' }}>
            <FileText size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
            <p>No audit logs found for the selected filters.</p>
          </div>
        ) : (
          logs.map((log, i) => {
            const details = parseDetails(log);
            const isExpanded = expandedRow === i;
            return (
              <div key={i} style={{ borderBottom: i < logs.length - 1 ? '1px solid var(--border-primary)' : 'none' }}>
                {/* Main Row */}
                <div
                  onClick={() => setExpandedRow(isExpanded ? null : i)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                    cursor: 'pointer', transition: 'background 0.15s',
                    background: isExpanded ? 'var(--bg-tertiary)' : 'transparent',
                  }}
                  onMouseEnter={e => !isExpanded && (e.currentTarget.style.background = 'var(--bg-tertiary)')}
                  onMouseLeave={e => !isExpanded && (e.currentTarget.style.background = 'transparent')}
                >
                  {/* Status indicator */}
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                    background: log.status === 'success' ? 'var(--success)' :
                                log.status === 'failure' ? 'var(--error)' :
                                log.status === 'blocked' ? 'var(--warning)' : 'var(--text-tertiary)',
                  }} />

                  {/* Time */}
                  <span style={{ color: 'var(--text-tertiary)', fontSize: 11, minWidth: 80, flexShrink: 0 }}>
                    {formatTime(log.timestamp || log.created_at)}
                  </span>

                  {/* Actor */}
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--text-secondary)', fontSize: 12, minWidth: 80, flexShrink: 0 }}>
                    {getActorIcon(log.actor_type)}
                    <span style={{ textTransform: 'capitalize' }}>{log.actor_type}</span>
                  </span>

                  {/* Action */}
                  <span style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, flex: 1 }}>
                    {actionLabel(log.action)}
                  </span>

                  {/* Admin/Target */}
                  <span style={{ color: 'var(--text-secondary)', fontSize: 11, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {log.admin_email || log.target_name || ', '}
                  </span>

                  {/* Status badge */}
                  <span style={{ flexShrink: 0 }}>{getStatusBadge(log.status)}</span>

                  {/* Expand icon */}
                  <span style={{ color: 'var(--text-tertiary)', flexShrink: 0 }}>
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </span>
                </div>

                {/* Expanded Detail Row */}
                {isExpanded && (
                  <div style={{
                    padding: '14px 20px 16px 52px', background: 'var(--bg-tertiary)',
                    borderTop: '1px solid var(--border-primary)', fontSize: 12,
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                      {/* Description */}
                      <div>
                        <div style={{ color: 'var(--text-tertiary)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Description</div>
                        {details.map((d, j) => (
                          <div key={j} style={{ color: 'var(--text-primary)', marginBottom: 4, lineHeight: 1.5 }}>{d}</div>
                        ))}
                      </div>

                      {/* Raw Data */}
                      <div>
                        <div style={{ color: 'var(--text-tertiary)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Details</div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: 11, lineHeight: 1.6 }}>
                          {log.admin_email && <div>Admin: {log.admin_email}</div>}
                          {log.target_name && <div>Target: {log.target_name}</div>}
                          {log.target_type && <div>Type: {log.target_type}</div>}
                          {log.ip_address && <div>IP: {log.ip_address}</div>}
                          {log.resource_type && <div>Resource: {log.resource_type}{log.resource_id ? ` #${log.resource_id}` : ''}</div>}
                        </div>
                      </div>

                      {/* Values */}
                      {(log.old_value || log.new_value) && (
                        <div>
                          <div style={{ color: 'var(--text-tertiary)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Changes</div>
                          <div style={{ fontSize: 11, lineHeight: 1.6 }}>
                            {log.old_value && (
                              <div style={{ color: 'var(--error)' }}>
                                <strong>Before:</strong> {log.old_value.length > 100 ? log.old_value.substring(0, 100) + '...' : log.old_value}
                              </div>
                            )}
                            {log.new_value && (
                              <div style={{ color: 'var(--success)', marginTop: 4 }}>
                                <strong>After:</strong> {log.new_value.length > 100 ? log.new_value.substring(0, 100) + '...' : log.new_value}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
        </div>
      </div>

      {/* Pagination */}
      {total > 50 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 20 }}>
          <button onClick={() => fetchLogs(page - 1)} disabled={page === 1} style={{
            padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-primary)',
            background: 'transparent', color: page === 1 ? 'var(--text-tertiary)' : 'var(--text-secondary)',
            cursor: page === 1 ? 'default' : 'pointer', fontSize: 12,
          }}>← Previous</button>
          <span style={{ color: 'var(--text-secondary)', fontSize: 13, alignSelf: 'center' }}>Page {page}</span>
          <button onClick={() => fetchLogs(page + 1)} disabled={logs.length < 50} style={{
            padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-primary)',
            background: 'transparent', color: logs.length < 50 ? 'var(--text-tertiary)' : 'var(--text-secondary)',
            cursor: logs.length < 50 ? 'default' : 'pointer', fontSize: 12,
          }}>Next →</button>
        </div>
      )}
    </div>
  );
}

const filterStyle = {
  padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border-primary)',
  background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 12,
  fontFamily: 'inherit', cursor: 'pointer', outline: 'none',
};
